import store from '@/store/store'
import { computed, unref } from 'vue'
import { Model } from '@/models'
import { DxpItemActions } from '@/enums'
import { DxpItemStatus, DxpItemType, QueueItemStatus } from '@/enums/graphql'
import { DxpInboxItemService } from '@/services'
import { getMatchInfo } from '@/helpers/dxp'

export class DxpInboxItem extends Model {
    /**
     * Create a DxpInboxItem model wrapper.
     * @param {Object} data - Object holding the field values.
     */
    constructor (data) {
        super()
        Model.initializeFields(this, [
            'id',
            'trace_id',
            'status',
            'service_provider_id',
            'igb2b_insurer_code',
            'fileInfo',
            'document_type_identifier',
            'file',
            'targetObject',
            'currentQueueItem',
            'queueItems',
        ], data)
        this.type = DxpItemType.INBOX
        this.isInboxItem = true
        this.isOutboxItem = false

        // Retry
        this.isEligibleForRetry = computed( () => {
            return [DxpItemStatus.DXP_ERROR, DxpItemStatus.ON_HOLD].includes(this.status) && [QueueItemStatus.FAILED].includes(this.currentQueueItem.status)
        })

        // Dismiss
        this.isEligibleForDismiss = computed(() => {
            return [DxpItemStatus.PENDING, DxpItemStatus.DXP_ERROR, DxpItemStatus.ON_HOLD, DxpItemStatus.UNPROCESSABLE].includes(this.status) && [QueueItemStatus.QUEUED, QueueItemStatus.FAILED, QueueItemStatus.DONE].includes(this.currentQueueItem.status)
        })

        // Update target object matched contract
        // TODO @MTR & @TFU: Verify condition.
        this.canUpdateTargetObjectMatchedContract = computed(() => {
            return !!this.targetObject && [DxpItemStatus.ON_HOLD, DxpItemStatus.DONE].includes(this.status)
        })

        // Aggregated status
        this.aggregatedStatus = computed(() => {
            if (this.status === DxpItemStatus.ON_HOLD && ['invalid_xml_file', 'timeout', 'unknown'].includes(this.currentQueueItem?.status_message?.code)) {
                return this.currentQueueItem?.status
            }

            switch (this.status) {
                case DxpItemStatus.PENDING:
                    return this.currentQueueItem?.status
                case DxpItemStatus.ON_HOLD:
                    return this.targetObject?.nextStep || this.status
                default:
                    return this.status
            }
        })

        // Matching-related info
        this.matchInfo = computed(() => {
            return getMatchInfo(this.targetObject)
        })
    }

    static get service () { return DxpInboxItemService }
    static action = DxpItemActions
    static type = DxpItemType

    get actions () {
        const actions = []

        // Update target object matched contract
        if (unref(this.canUpdateTargetObjectMatchedContract)) {
            actions.push({
                key: DxpItemActions.UPDATE_TARGET_OBJECT_MATCHED_CONTRACT,
                item: this,
            })
        }

        // Unassign target object matched contract
        if (this.status === DxpItemStatus.DONE && this.targetObject?.matchedContract) {
            actions.push({
                key: DxpItemActions.UNASSIGN_TARGET_OBJECT_MATCHED_CONTRACT,
                item: this,
            })
        }

        // Retry
        if (unref(this.isEligibleForRetry)) {
            actions.push({
                key: DxpItemActions.RETRY,
                item: this,
            })
        }

        // Dismiss
        if (unref(this.isEligibleForDismiss)) {
            actions.push({
                key: DxpItemActions.DISMISS,
                item: this,
            })
        }

        return actions
    }

    get nextStep () {
        if ([DxpItemStatus.DONE, DxpItemStatus.DISMISSED].includes(this.status)) return null
        return this.targetObject?.nextStep
    }

    get service_provider_display_name() {
        return store.state.dxpServiceProviderConfigurationDisplayNames[this.service_provider_id] ?? ''
    }

    /**
     * Update the matched contract for this dxpInboxItem's targetObject.
     * @param {string} contractId - The ID of the contract to be matched.
     * @returns {Promise<*>} - A promise that resolves with the updated dxpInboxItem.
     */
    updateTargetObjectMatchedContract (contractId) {
        return DxpInboxItemService.updateTargetObjectMatchedContract({
            dxpInboxItemId: this.id,
            contractId,
        })
    }
}
